import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import emailjs from '@emailjs/browser';

export default function PortalGlobalNotificaciones() {
  const [edificios, setEdificios] = useState([]);
  const [edificioDestino, setEdificioDestino] = useState('todos'); // 'todos' o un id de edificio específico
  const [titulo, setTitulo] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [archivo, setArchivo] = useState(null); // 📂 Estado para la imagen o PDF adjunto
  
  const [borradores, setBorradores] = useState([]); // 📋 Lista de comunicados y borradores
  const [itemPrevisualizando, setItemPrevisualizando] = useState(null); // 👁️ Vista previa
  const [enviandoId, setEnviandoId] = useState(null); // ⏳ ID del comunicado en envío

  const fechaActual = new Date().toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  useEffect(() => {
    const cargarEdificios = async () => {
      try {
        const snapEdif = await getDocs(collection(db, 'edificios_mmdl'));
        const listaEdif = snapEdif.docs.map(d => ({ id: d.id, ...d.data() }));
        setEdificios(listaEdif);
      } catch (error) {
        console.error("Error al cargar edificios:", error);
      }
    };
    cargarEdificios();
  }, []);

  // 📝 Crear el comunicado y guardarlo en la bandeja
  const crearCorreoBorrador = (e) => {
    e.preventDefault();
    if (!titulo.trim() || !mensaje.trim()) {
      alert("Por favor completa el título y el mensaje de la notificación.");
      return;
    }

    const nombreDestinoTexto = edificioDestino === 'todos' 
      ? '🌐 Todos los Edificios (Masivo)' 
      : `🏢 ${edificios.find(e => e.id === edificioDestino)?.nombre || 'Edificio Seleccionado'}`;

    const nuevoBorrador = {
      id: Date.now(),
      titulo: titulo.trim(),
      mensaje: mensaje.trim(),
      archivo: archivo,
      archivoUrl: '',       // Se llenará al enviar
      nombreArchivo: archivo ? archivo.name : '',
      edificioDestino,
      nombreDestinoTexto,
      fecha: fechaActual,
      enviado: false,       // Estado inicial: borrador
      idsEnFirestore: []    // Almacenará los IDs de los documentos creados al enviar
    };

    setBorradores([...borradores, nuevoBorrador]);
    setTitulo('');
    setMensaje('');
    setArchivo(null);
    
    const inputFile = document.getElementById('inputArchivoGlobal');
    if (inputFile) inputFile.value = '';

    alert("📝 ¡Comunicado creado y guardado en la bandeja!");
  };

  // 🗑️ Borrar comunicado de la bandeja y de Firestore (Portal de Propietarios)
  const borrarBorrador = async (item) => {
    const mensajeConfirmacion = item.enviado 
      ? "⚠️ ¿Estás seguro de eliminar este comunicado? Esto lo borrará del portal de los propietarios y de tu bandeja."
      : "¿Estás seguro de eliminar este borrador de la lista?";

    if (window.confirm(mensajeConfirmacion)) {
      try {
        if (item.enviado && item.idsEnFirestore && item.idsEnFirestore.length > 0) {
          for (let target of item.idsEnFirestore) {
            await deleteDoc(doc(db, `notificaciones_${target.edificioId}`, target.docId));
          }
        }

        setBorradores(borradores.filter(b => b.id !== item.id));
        alert("🗑️ ¡Comunicado eliminado con éxito de la bandeja y del portal web!");
      } catch (error) {
        console.error("Error al eliminar notificación de Firestore:", error);
        alert("❌ Hubo un error al intentar eliminar el comunicado de la base de datos.");
      }
    }
  };

  // 🚀 Enviar notificación a Firestore y disparar correos vía EmailJS
  const enviarCorreoDeLista = async (item) => {
    try {
      setEnviandoId(item.id);
      let archivoUrl = item.archivoUrl || '';

      // ☁️ Subir archivo adjunto a Firebase Storage si aún no se había subido
      if (!archivoUrl && item.archivo) {
        const storageRef = ref(storage, `comunicados_globales/${Date.now()}_${item.archivo.name}`);
        const snapshot = await uploadBytes(storageRef, item.archivo);
        archivoUrl = await getDownloadURL(snapshot.ref);
      }

      // Mensaje estructurado con la firma formal corporativa
      const mensajeConFirma = `${item.mensaje}\n\nAtentamente,\nLa Administración`;

      const datosNotificacion = {
        titulo: item.titulo,
        mensaje: mensajeConFirma,
        archivoUrl: archivoUrl,
        nombreArchivo: item.nombreArchivo,
        fecha: item.fecha,
        remitente: 'Administración General (MMD&L)'
      };

      let listaEdificiosActuales = edificios;
      if (listaEdificiosActuales.length === 0) {
        const snapEdif = await getDocs(collection(db, 'edificios_mmdl'));
        listaEdificiosActuales = snapEdif.docs.map(d => ({ id: d.id, ...d.data() }));
      }

      let edificiosAProcesar = [];
      if (item.edificioDestino === 'todos') {
        edificiosAProcesar = listaEdificiosActuales;
      } else {
        const edifUnico = listaEdificiosActuales.find(e => e.id === item.edificioDestino);
        if (edifUnico) edificiosAProcesar = [edifUnico];
      }

      if (edificiosAProcesar.length === 0) {
        alert("⚠️ No se encontraron edificios de destino válidos.");
        setEnviandoId(null);
        return;
      }

      let nuevosIdsFirestore = item.idsEnFirestore || [];

      // Si es la primera vez que se envía, guardamos en Firestore
      if (!item.enviado) {
        for (let edif of edificiosAProcesar) {
          const docRef = await addDoc(collection(db, `notificaciones_${edif.id}`), datosNotificacion);
          nuevosIdsFirestore.push({ edificioId: edif.id, docId: docRef.id });
        }
      }

      // 2. Enviar/Reenviar correos a propietarios vía EmailJS
      for (let edif of edificiosAProcesar) {
        try {
          const snapPropietarios = await getDocs(collection(db, `propietarios_${edif.id}`));
          const listaPropietarios = snapPropietarios.docs.map(d => d.data());

          for (let prop of listaPropietarios) {
            const correoDestino = prop.correo || prop.email;
            if (correoDestino && correoDestino.includes('@')) {
              const templateParams = {
                to_email: correoDestino,
                to_name: prop.nombre || 'Residente',
                building_name: edif.nombre || 'Edificio MMD&L',
                titulo_aviso: item.titulo,
                mensaje_aviso: mensajeConFirma,
                title: item.titulo,
                message: mensajeConFirma,
                attachment_link: archivoUrl ? archivoUrl : 'No hay archivos adjuntos en este comunicado.',
                date: item.fecha
              };

              try {
                await emailjs.send(
                  'service_kdfys3f', 
                  'template_dqh1wim', 
                  templateParams, 
                  'pfJqVpoChzMWTp7L9'
                );
              } catch (errEmail) {
                console.error("Detalle del error de EmailJS para", correoDestino, ":", errEmail.text || errEmail);
              }
            }
          }
        } catch (errProp) {
          console.error(`Error al procesar propietarios del edificio ${edif.nombre}:`, errProp);
        }
      }

      // Actualizar el estado del elemento en la bandeja
      setBorradores(borradores.map(b => b.id === item.id ? { ...b, enviado: true, archivoUrl, idsEnFirestore: nuevosIdsFirestore } : b));

      alert(item.enviado ? "🔄 ¡Correos reenviados con éxito!" : "✅ ¡Comunicado publicado en el portal web y correos enviados con éxito!");

    } catch (error) {
      console.error("Error al procesar comunicado:", error);
      alert("❌ Hubo un error al procesar la solicitud.");
    } finally {
      setEnviandoId(null);
    }
  };

  return (
    <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '25px', textAlign: 'left', maxWidth: '750px', margin: '0 auto' }}>
      <h3 style={{ marginTop: 0, color: '#1e293b' }}>📢 Enviar Notificaciones / Comunicados Globales</h3>
      <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px' }}>
        Crea tus comunicados, revísalos mediante vista previa y envíalos simultáneamente al portal web y por correo electrónico.
      </p>

      {/* Formulario de Creación */}
      <form onSubmit={crearCorreoBorrador} style={{ display: 'flex', flexDirection: 'column', gap: '18px', backgroundColor: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
        <h4 style={{ margin: '0 0 5px 0', color: '#0f172a', fontSize: '1rem' }}>✍️ Redactar Nuevo Comunicado</h4>
        
        {/* --- SECCIÓN LLAMATIVA PARA EL EDIFICIO DESTINO --- */}
        <div style={{ backgroundColor: '#eff6ff', border: '2px solid #3b82f6', borderRadius: '8px', padding: '15px' }}>
          <label style={{ fontSize: '0.95rem', fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#1e40af' }}>
            🏢 SELECCIONA EL DESTINO DEL COMUNICADO (¡Verifica bien antes de enviar!):
          </label>
          <select 
            value={edificioDestino} 
            onChange={(e) => setEdificioDestino(e.target.value)}
            style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '2px solid #2563eb', fontSize: '1.05rem', fontWeight: 'bold', backgroundColor: 'white', color: '#1e293b', cursor: 'pointer' }}
          >
            <option value="todos">🌐 TODOS LOS EDIFICIOS (Envío Masivo Global)</option>
            {edificios.map(edif => (
              <option key={edif.id} value={edif.id}>🏢 {edif.nombre}</option>
            ))}
          </select>
        </div>
        {/* ------------------------------------------------ */}

        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '6px', color: '#334155' }}>
            Título del Comunicado:
          </label>
          <input 
            type="text" 
            placeholder="Ej. Mantenimiento general de instalaciones..." 
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box', backgroundColor: 'white' }}
            required
          />
        </div>

        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '6px', color: '#334155' }}>
            Mensaje o Detalle:
          </label>
          <textarea 
            placeholder="Escribe aquí el mensaje que verán los propietarios..." 
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            rows={4}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box', resize: 'vertical', backgroundColor: 'white' }}
            required
          />
        </div>

        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '6px', color: '#334155' }}>
            Archivo Adjunto (Opcional - Imagen o PDF):
          </label>
          <input 
            id="inputArchivoGlobal"
            type="file" 
            accept="image/*,.pdf"
            onChange={(e) => setArchivo(e.target.files[0])}
            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', boxSizing: 'border-box' }}
          />
        </div>

        <button 
          type="submit" 
          style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '6px', fontWeight: 'bold', fontSize: '0.95rem', cursor: 'pointer', alignSelf: 'flex-start' }}
        >
          ➕ Crear Comunicado / Borrador
        </button>
      </form>

      {/* --- 📋 BANDEJA DE COMUNICADOS Y BORRADORES --- */}
      <div style={{ marginTop: '30px' }}>
        <h4 style={{ color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>
          📋 Bandeja de Comunicados ({borradores.length})
        </h4>

        {borradores.length === 0 ? (
          <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.9rem' }}>No hay comunicados en la bandeja en este momento.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '15px' }}>
            {borradores.map(item => (
              <div key={item.id} style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '0.75rem', backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                      {item.nombreDestinoTexto}
                    </span>
                    <span style={{ fontSize: '0.75rem', backgroundColor: item.enviado ? '#dcfce7' : '#fef3c7', color: item.enviado ? '#166534' : '#d97706', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                      {item.enviado ? 'Enviado ✅' : 'Borrador 📝'}
                    </span>
                  </div>
                  <h5 style={{ margin: '6px 0 4px 0', color: '#1e293b', fontSize: '1rem' }}>{item.titulo}</h5>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.8rem' }}>📅 Fecha: {item.fecha} {item.nombreArchivo ? `| 📎 ${item.nombreArchivo}` : ''}</p>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => setItemPrevisualizando(item)}
                    style={{ backgroundColor: '#475569', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    👁️ Vista Previa
                  </button>

                  {!item.enviado ? (
                    <button 
                      onClick={() => enviarCorreoDeLista(item)}
                      disabled={enviandoId === item.id}
                      style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      {enviandoId === item.id ? '⏳ Enviando...' : '🚀 Enviar Web y Correo'}
                    </button>
                  ) : (
                    <button 
                      onClick={() => enviarCorreoDeLista(item)}
                      disabled={enviandoId === item.id}
                      style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer' }}
                      title="Volver a enviar los correos de este comunicado"
                    >
                      {enviandoId === item.id ? '⏳ Reenviando...' : '🔄 Reenviar'}
                    </button>
                  )}

                  <button 
                    onClick={() => borrarBorrador(item)}
                    style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    🗑️ Borrar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- 👁️ VENTANA MODAL DE VISTA PREVIA --- */}
      {itemPrevisualizando && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center',
          alignItems: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '600px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)', overflow: 'hidden', display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ backgroundColor: '#0f172a', color: 'white', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>📧 Vista Previa del Comunicado</h3>
              <button 
                onClick={() => setItemPrevisualizando(null)} 
                style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.2rem', cursor: 'pointer', fontWeight: 'bold' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '25px', backgroundColor: '#f8fafc', overflowY: 'auto', maxHeight: '70vh' }}>
              <div style={{ backgroundColor: '#e2e8f0', padding: '10px 15px', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '20px', color: '#334155' }}>
                <p style={{ margin: '0 0 3px 0' }}><strong>De:</strong> Administración MMD&L &lt;no-reply@mmdl.com&gt;</p>
                <p style={{ margin: '0 0 3px 0' }}><strong>Para:</strong> Residentes ({itemPrevisualizando.nombreDestinoTexto})</p>
                <p style={{ margin: 0 }}><strong>Asunto:</strong> {itemPrevisualizando.titulo}</p>
              </div>

              <div style={{ backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '20px', color: '#1e293b' }}>
                <h3 style={{ color: '#0284c7', marginTop: 0, fontSize: '1.1rem' }}>🏢 MMD&L - Comunicado Oficial</h3>
                <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '12px 0' }} />
                
                <p style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Estimado propietario / residente,</p>
                
                <p style={{ fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                  {itemPrevisualizando.mensaje}
                  {'\n\nAtentamente,\nLa Administración'}
                </p>

                {itemPrevisualizando.archivo && (
                  <div style={{ backgroundColor: '#f1f5f9', padding: '10px', borderRadius: '6px', marginTop: '15px', fontSize: '0.82rem', color: '#0284c7', fontWeight: 'bold' }}>
                    📎 Archivo adjunto: {itemPrevisualizando.archivo.name}
                  </div>
                )}

                <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '15px 0' }} />
                <p style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center', margin: 0 }}>
                  Enviado el {itemPrevisualizando.fecha} por Administración General (MMD&L)
                </p>
              </div>
            </div>

            <div style={{ padding: '15px 20px', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
              <button 
                onClick={() => setItemPrevisualizando(null)}
                style={{ backgroundColor: '#64748b', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Cerrar Vista Previa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}